// @flow
import React, { Component, Fragment } from "react";
import {
  get,
  isEqual,
  some,
  noop,
  attempt,
  size,
  sumBy,
  toArray,
  isError,
  isEmpty,
  words,
  set,
  flowRight as compose
} from "lodash";
import { connect } from "react-redux";
import type { Dispatch } from "redux";
import { RouterHistory } from "react-router";
import mediator from "@tvg/mediator";
import wtx, { type selectionsType } from "@tvg/api/wtx";
import calculateTotals from "@tvg/api/wtx/BetHelper";
import tvgConf from "@tvg/conf";
import type { TvgConf } from "@tvg/conf/src/types";
import performanceUtils from "@tvg/utils/performanceUtils";
import type { BetTicketState } from "@fdr/types/Bet";
import sendAppMetrics from "@tvg/metrics-collector";
import BetUtils, { mapSelectionsToNumber } from "@tvg/utils/betSelectionUtils";
import { mapSelectionsToString } from "@tvg/utils/talentPicksUtils";
import { type NullaryFn } from "@tvg/types/Functional";
import {
  triggerRefreshSession,
  onOpenComplianceModal,
  onTriggerDeposit
} from "@tvg/sh-lib-account-wallet/src/utils/mobileUtils";
import {
  isMobile,
  isXSell,
  onTriggerGeolocation
} from "@tvg/sh-utils/mobileUtils";
import { multiLegWagerTypes } from "@fdr/utils/wagerUtils";
import {
  getAuthTokenCookie,
  getGeoTokenCookie,
  isAccountCompliantSelector
} from "@tvg/sh-utils/sessionUtils";
import { setHasMultiLegWagersAction } from "@fdr/shared-actions/ProgramPageActions";
import { setBetTicketState } from "@fdr/shared-actions/BetActions";
import {
  resetBetting,
  resetSelection,
  setBetConfirmation,
  setBetAmount,
  setPickLeg,
  setSelectionScratched,
  setPickAction,
  noMandatoryTalentIdSelected,
  setBetType
} from "@tvg/shared-actions/BetActions";
import { updateBalance } from "@tvg/shared-actions/UserActions";
import { openInvalidResidentialAddressModal } from "@fdr/shared-actions/UserActions";
import type {
  RaceProgram,
  RaceWagerType,
  BettingInterest,
  Probable
} from "@tvg/types/Race";
import type { TalentPickData } from "@tvg/types/TalentPicks";
import {
  INELIGIBLE_RESIDENCY_STATE_FOR_RACING,
  INELIGIBLE_RESIDENCY_STATE_FOR_RACING_MESSAGE,
  TERMS_AND_CONDITIONS_OR_ACKNOWLEDGES_NOT_ACCEPTED,
  INELIGIBLE_LOCATION,
  GEO_COMPLY_REJECTED,
  GEO_COMPLY_EXPIRED
} from "@tvg/api/uam";
import BetTicket from "@fdr/atomic-ui/_templates/BetTicket";
import TalentPickConfirmationHeader from "@fdr/atomic-ui/_molecules/TalentPickConfirmationHeader";
import Toast from "@fdr/atomic-ui/_atoms/Toast";
import { allowLocationSplashAction } from "@urp/location-splash/src/actions";
import type { LocationStatusTypes } from "@urp/location-splash/src/types";
import { FeedbackMessageContext } from "@fdr/utils/contextUtils";
import {
  doesRaceWagerTypeHasProbables,
  getCurrentProbableValue
} from "@fdr/utils/potentialReturnUtils";
import { parseJSONCapiMessage } from "@fdr/utils/parseJSONCapiMessage";
import type {
  GeolocationStatus,
  GeolocationError
} from "@tvg/sh-geolocation/src/types";
import type { ToastProps } from "@fdr/utils/generalTypes";
import sendToAppsFlyer from "@tvg/utils/appsflyerUtils";
import RaceHeaderUtils from "@tvg/utils/RaceHeaderUtils";
import {
  replaceCAPIVariables,
  CAPIVariableRegex,
  extractNumberOfText
} from "@fdr/utils/capiUtils";
import {
  clearSession,
  logout
} from "@tvg/sh-lib-account-wallet/src/session/logout";
import {
  getGeolocationMessageShown,
  getGeolocationStatus,
  getGeolocationError,
  getGeolocationErrorRetryable
} from "@tvg/sh-geolocation/src/redux/selectors";
import {
  geolocationStatusClear,
  setGeolocationError
} from "@tvg/sh-geolocation/src/redux/actions";
import { getUserProfile } from "@tvg/sh-lib-account-wallet/src/session/login";
import { getWalletBalances } from "@tvg/sh-utils/walletUtils";

import { events as AlchemerEvents } from "@urp/alchemer";
import { getAccountNumber, getBalance } from "@urp/store-selectors";
import { getUserJurisdiction } from "@tvg/sh-utils/userJurisdictionUtils";
import BetConfirmationModal from "./BetConfirmationModalComponent";
import {
  asyncSubmit,
  BetError,
  BetStatus,
  getBetDetails,
  getBetSelectionSpecialGroup,
  getBetSelectionsToShow
} from "./utils";
import {
  type BetGroupStatus,
  setGroupBetTicketStatusAction,
  setGroupBetTicketStopAction,
  setGroupBetTicketCountersResetAction,
  setGroupBetTicketStatusClearAction
} from "./actions";

type SubmitBet = {
  wagerType: RaceWagerType,
  betSelections: string[][],
  wagerSelection: selectionsType,
  betAmount: number,
  race: RaceProgram
};

type State = {
  forceBetConfirm: boolean,
  retryBetCounter: number
};

type Props = {
  dispatch: Dispatch<*>,
  history: RouterHistory,
  betTicketState: BetTicketState,
  hasUserPreferences: boolean,
  showBetConfirmation: boolean,
  isVisible: boolean,
  betSelections: string[][],
  scratchedSelections: string[][],
  bettingInterests: BettingInterest[][],
  race: RaceProgram,
  races?: RaceProgram[],
  probables?: Probable[],
  wagerType: RaceWagerType,
  betAmount: number,
  isLogged: boolean,
  isAccountCompliant: boolean,
  showBetConfirmationPreference: "0" | "1",
  locationStatus: LocationStatusTypes,
  isLocationRequired: boolean,
  isRequestingLocation: boolean,
  isVideoShown: boolean,
  isMultiLeg: boolean,
  geolocation: {
    location: {
      latitude: ?number,
      longitude: ?number
    },
    regions: Array<{
      description: string,
      id: number,
      name: string,
      type: string
    }>,
    state: string
  },
  columnLayout: "single" | "dual",
  favoriteSelections: string[][],
  selectedTalentId: number,
  selectTalentIdErrorVisible: boolean,
  talentPick: ?TalentPickData,
  talentPickTriggerOrigin: ?("programpage" | "pickspage"),
  pickEditButton: boolean,
  wtxErrorMessages: string,
  wtxErrorMessagesWithBehaviour: string,
  isBetMessagesWithBehaviour: boolean,
  betLeg: number,
  potentialReturnProgramPage: boolean,
  accountNumber: string,
  onboardingTooltipShow: boolean,
  onCloseOnboardingTooltip: NullaryFn<*>,
  betTicketGroupStatus: BetGroupStatus | null,
  betTicketProcessing: number,
  betTicketErrorBets: number,
  balance: ?number,
  betTicketForceStopProcessing: boolean,
  pickBetsOnboardingToggle: boolean,
  watchButtonBetPlacedToggle: boolean,
  descriptionsWinBetGroupMessage?: {
    WP: string,
    WPS: string,
    INT: string,
    EmptyTicket: string,
    WS: string
  },
  geolocationStatus?: GeolocationStatus,
  geolocationError?: GeolocationError,
  geoLocationToastMsg?: { [key: GeolocationStatus]: ToastProps },
  geolocationMessageShown?: boolean,
  geolocationBetErrorMsg?: {
    title: string,
    text: string
  },
  unverifiedUserBetErrorMsg?: {
    title: string,
    text: string
  },
  geolocationErrorRetryable?: boolean,
  isBalanceShown?: boolean
};

const isScratchedSelections = (selections: string[][]): boolean =>
  some(selections, (selection) => selection.length > 0);

export const getTitleScratchedRunners = (
  scratchedSelections: string[][] = [[]]
): string => {
  if (scratchedSelections.length > 1) {
    return scratchedSelections
      .map((selection, index) => {
        if (selection.length > 0) {
          return `Race ${index + 1} - Horse ${selection.join(
            ", "
          )} just got scratched`;
        }
        return selection;
      })
      .join(" ");
  }

  return `Runner ${scratchedSelections[0].join(", ")} just got scratched`;
};

export const sessionBetNameLogin = "placeBet";

export const onBoardingClickCTAGTM = (buttonCopy: string) => {
  mediator.base.dispatch({
    type: "PICK_BETS_ONBOARDING_CLICK_CTA",
    payload: { buttonCopy }
  });
};

export class BetTicketComponent extends Component<Props, State> {
  static defaultProps = {
    betSelections: [[]],
    scratchedSelections: [[]],
    favoriteSelections: [[]],
    isLogged: false,
    isAccountCompliant: false,
    races: [],
    probables: [],
    hasUserPreferences: false,
    showBetConfirmationPreference: "1",
    showBetConfirmation: false,
    betAmount: 0,
    isVisible: false,
    locationStatus: "empty",
    isLocationRequired: false,
    isRequestingLocation: false,
    columnLayout: "single",
    isVideoShown: false,
    isMultiLeg: false,
    selectTalentIdErrorVisible: false,
    selectedTalentId: -1,
    talentPick: null,
    talentPickTriggerOrigin: null,
    pickEditButton: false,
    wtxErrorMessages: "",
    wtxErrorMessagesWithBehaviour: "",
    isBetMessagesWithBehaviour: false,
    betTicketState: {},
    betLeg: 0,
    potentialReturnProgramPage: false,
    accountNumber: "",
    onboardingTooltipShow: true,
    onCloseOnboardingTooltip: noop,
    betTicketGroupStatus: null,
    betTicketProcessing: 0,
    betTicketErrorBets: 0,
    balance: undefined,
    betTicketForceStopProcessing: false,
    pickBetsOnboardingToggle: false,
    watchButtonBetPlacedToggle: false,
    descriptionsWinBetGroupMessage: {
      WP: "",
      WPS: "",
      INT: "",
      EmptyTicket: "",
      WS: ""
    },
    geoLocationToastMsg: {},
    geolocationError: {},
    geolocationMessageShown: false,
    geolocationBetErrorMsg: {
      title: "",
      text: ""
    },
    unverifiedUserBetErrorMsg: {
      title: "",
      text: ""
    }
  };

  tvgConf: TvgConf;

  constructor(props: Props): void {
    super(props);
    this.state = {
      forceBetConfirm: false,
      retryBetCounter: 0
    };
    this.tvgConf = tvgConf();
  }

  componentDidMount() {
    if (typeof window !== "undefined") {
      document.addEventListener("focusout", () => {
        window.scrollBy(0, 0);
      });
    }

    triggerRefreshSession();
  }

  shouldComponentUpdate(nextProps: Props) {
    return (
      nextProps.betLeg !== this.props.betLeg ||
      !isEqual(nextProps.race, this.props.race) ||
      !isEqual(nextProps.betSelections, this.props.betSelections) ||
      !isEqual(nextProps.bettingInterests, this.props.bettingInterests) ||
      !isEqual(nextProps.showBetConfirmation, this.props.showBetConfirmation) ||
      nextProps.betAmount !== this.props.betAmount ||
      !isEqual(nextProps.wagerType, this.props.wagerType) ||
      nextProps.isLogged !== this.props.isLogged ||
      nextProps.isVisible !== this.props.isVisible ||
      nextProps.isVideoShown !== this.props.isVideoShown ||
      nextProps.selectTalentIdErrorVisible !==
        this.props.selectTalentIdErrorVisible ||
      nextProps.selectedTalentId !== this.props.selectedTalentId ||
      !isEqual(nextProps.favoriteSelections, this.props.favoriteSelections) ||
      !isEqual(nextProps.scratchedSelections, this.props.scratchedSelections) ||
      !isEqual(this.props.geolocation, nextProps.geolocation) ||
      this.props.locationStatus !== nextProps.locationStatus ||
      this.props.isRequestingLocation !== nextProps.isRequestingLocation ||
      !isEqual(nextProps.talentPick, this.props.talentPick) ||
      !isEqual(nextProps.betTicketState, this.props.betTicketState) ||
      this.props.hasUserPreferences !== nextProps.hasUserPreferences ||
      !isEqual(this.props.probables, nextProps.probables) ||
      !isEqual(
        this.props.onboardingTooltipShow,
        nextProps.onboardingTooltipShow
      ) ||
      !isEqual(
        this.props.betTicketGroupStatus,
        nextProps.betTicketGroupStatus
      ) ||
      nextProps.geolocationStatus !== this.props.geolocationStatus ||
      nextProps.isAccountCompliant !== this.props.isAccountCompliant
    );
  }

  componentDidUpdate(prevProps: Props) {
    if (
      get(this.props, "race.raceNumber") !== get(prevProps, "race.raceNumber")
    ) {
      this.resetState();
    }

    if (
      get(prevProps, "locationStatus") === "pending" &&
      get(this.props, "locationStatus") === "success"
    ) {
      this.placeBetFunction();
    }

    if (
      get(prevProps, "showBetConfirmation") !==
        get(this.props, "showBetConfirmation") &&
      get(this.props, "showBetConfirmation") === false
    ) {
      const modalAnimationDelay = 200;
      this.handleCloseWarning();
      setTimeout(() => {
        this.setForceBetConfirmation(false);
      }, modalAnimationDelay);
    }

    triggerRefreshSession();
  }

  componentWillUnmount() {
    this.resetState();

    if (typeof window !== "undefined") {
      document.removeEventListener("focusout", () => {
        window.scrollBy(0, 0);
      });
    }
  }

  onRetainRunners = () => {
    this.props.dispatch(setBetConfirmation(false));
    this.props.dispatch(setBetTicketState({ repeatBetNumber: 1 }));
    this.props.dispatch(setPickAction(null));
    // this timeout is for animations to roll and prevent flicker
    setTimeout(this.resetState, 400);

    mediator.base.dispatch({
      type: "BET_CONFIRMATION:RETAIN_RUNNERS",
      payload: {
        trackName: get(this.props, "race.track.trackName"),
        raceNumber: get(this.props, "race.raceNumber"),
        betType: get(this.props, "wagerType.type.code"),
        runnerType:
          get(this.props, "race.type.code") === "G" ? "Greyhound" : "Horse"
      }
    });
  };

  onRetryBets = () => {
    this.resetState();

    this.props.dispatch(
      setBetTicketState({
        betConfirmationTitle: "retry bet"
      })
    );

    this.props.dispatch(setGroupBetTicketCountersResetAction());

    const bets = toArray(this.props.betTicketGroupStatus)
      .filter((bet) => bet.status === "ERROR")
      .map((bet) => {
        const wagerTypeCode = get(bet.retryBet, "wagerType.type.code", "");

        // --------- add overlay processing on betTicket
        this.props.dispatch(
          setGroupBetTicketStatusAction(wagerTypeCode, BetStatus.processing, {})
        );

        const totals = calculateTotals(
          bet.retryBet.betAmount,
          get(bet, "retryBet.wagerType", {
            type: { name: "", id: "" }
          }),
          bet.retryBet.betSelections
        );

        mediator.base.dispatch({
          type: "BET_CONFIRMATION:RETRY_BET",
          payload: {
            selections: get(bet.retryBet, "betSelections", []).join(", "),
            trackName: get(bet.retryBet, "race.track.trackName"),
            raceNumber: get(bet.retryBet, "race.raceNumber"),
            betAmount: totals.betCount * bet.retryBet.betAmount,
            runnerAmount: get(bet.retryBet, "betAmount"),
            betType: get(bet.retryBet, "wagerType.type.code"),
            runnerType:
              get(bet.retryBet.race, "type.code") === "G"
                ? "Greyhound"
                : "Horse"
          }
        });

        return async () => this.submitBet(bet.retryBet);
      });

    asyncSubmit(bets);
  };

  onBackToRace = () => {
    this.props.dispatch(setBetConfirmation(false));
    this.props.dispatch(setBetTicketState({ repeatBetNumber: 1 }));
    this.props.dispatch(setPickAction(null));

    // this timeout is for animations to roll and prevent flicker
    setTimeout(() => {
      this.props.dispatch(resetSelection());
      this.props.dispatch(setPickLeg(0));
      this.props.dispatch(setSelectionScratched([[]]));
      this.props.dispatch(
        setBetTicketState({
          isBetPlaced: false,
          betConfirmationTitle: "confirm bet",
          randomTalentPhrase: "",
          randomTalentPhraseId: -1
        })
      );
    }, 400);
  };

  onCloseCustomAmountModal = () => {
    mediator.base.dispatch({
      type: "PPM_CLOSE_CUSTOM_AMOUNT"
    });
    this.forceUpdate();
  };

  setForceBetConfirmation = (forceBetConfirm: boolean) => {
    this.setState({
      forceBetConfirm
    });
  };

  setBetAmount = (value: number, isCustomAmount: boolean) => {
    this.props.dispatch(setBetAmount(parseFloat(value)));

    mediator.base.dispatch({
      type: "PPM_SET_BET_AMOUNT",
      payload: {
        betAmount: value,
        isCustomAmount
      }
    });
  };

  getMessageFormatted = (
    errorCode: string = "default",
    partialMessage: boolean = true,
    forceStopProcessing: boolean = false
  ) => {
    const betConfirmationTitle = "Bet not placed!";
    const betTicketStatus = get(this.props, "betTicketGroupStatus", {});
    const betTicketsTotal = size(betTicketStatus);
    const betTicketError = get(this.props, "betTicketErrorBets", 0);
    const forceStop =
      forceStopProcessing ||
      get(this.props, "betTicketForceStopProcessing", false);
    let errorProps = {
      isProcessing: false,
      isBetPlaced: true,
      betConfirmationTitle: "Bet placed!",
      randomTalentPhrase: "Good luck!",
      randomTalentPhraseId: 0
    };

    let errorType = "default";
    if (forceStop || this.props.betTicketErrorBets > 0) {
      const multipleBets = partialMessage && betTicketsTotal > 1;
      if (!forceStop && multipleBets) {
        let errorInAllKey = "all";
        let errorInSomeKey = "multiple";

        if (!this.props.isBetMessagesWithBehaviour) {
          errorInAllKey = "groupFullError";
          errorInSomeKey = "groupSomeError";
        }

        errorType =
          betTicketError >= betTicketsTotal ? errorInAllKey : errorInSomeKey;
      }

      errorProps = {
        isProcessing: false,
        isBettingError: true,
        betConfirmationTitle,
        errorBetMessage: this.parseErrorMessage(errorCode, errorType)
      };
    }

    return errorProps;
  };

  resetState = () => {
    this.props.dispatch(
      setBetTicketState({
        isProcessing: false,
        isBetPlaced: false,
        isBettingError: false,
        errorBetMessage: {
          title: "",
          text: ""
        },
        betConfirmationTitle: "confirm bet",
        randomTalentPhrase: "",
        randomTalentPhraseId: -1
      })
    );
    this.props.dispatch(setGroupBetTicketStatusClearAction());
  };

  handleCloseWarning = () => {
    this.props.dispatch(setSelectionScratched([[]]));
  };

  cancelBet = (isTrashClicked?: boolean) => {
    if (
      this.props.showBetConfirmation &&
      this.props.betTicketState.isBetPlaced
    ) {
      this.props.dispatch(setBetConfirmation(false));
      this.props.dispatch(setBetTicketState({ repeatBetNumber: 1 }));
    } else {
      this.props.dispatch(resetBetting());
      this.resetState();
    }

    if (isTrashClicked) {
      const wt = BetUtils.getDefaultBetType(this.props.race, "", false);
      this.props.dispatch(setBetType(wt));

      mediator.base.dispatch({
        type: "PPM_DELETE_BET_TICKET",
        payload: {
          trackName: get(this.props, "race.track.trackName"),
          raceNumber: get(this.props, "race.raceNumber"),
          betType: get(this.props, "wagerType.group.name"),
          runnerType: get(this.props, "race.isGreyhound")
            ? "Greyhounds"
            : "Horse"
        }
      });
    }

    triggerRefreshSession();
  };

  placeBetFunction = () => {
    this.resetState();
    if (this.props.showBetConfirmationPreference === "0") {
      this.handleSubmitBet();
    } else {
      this.props.dispatch(setBetConfirmation(true));
      this.props.dispatch(
        setBetTicketState({
          betConfirmationTitle: "confirm bet"
        })
      );
    }
  };

  handleBetNow = () => {
    const { isLogged, isAccountCompliant, isLocationRequired, dispatch } =
      this.props;

    mediator.base.dispatch({
      type: "PPM_BET_NOW",
      payload: {
        selections: get(this.props, "betSelections").join(", "),
        trackName: get(this.props, "race.track.trackName"),
        raceNumber: get(this.props, "race.raceNumber"),
        betAmount: get(this.props, "betAmount"),
        betType: get(this.props, "wagerType.type.code"),
        runnerType:
          get(this.props, "race.type.code") === "G" ? "Greyhound" : "Horse"
      }
    });
    if (isLogged && !isAccountCompliant) {
      onOpenComplianceModal();
      return;
    }

    if (!isLogged) {
      mediator.base.dispatch({
        type: "OPEN_LOGIN",
        payload: {
          callback: this.placeBetFunction
        }
      });
    } else if (
      !isLocationRequired ||
      (get(this.props, "geolocation.location.latitude", null) &&
        get(this.props, "geolocation.location.longitude", null) &&
        get(this.props, "geolocation.state", null))
    ) {
      this.placeBetFunction();
    } else {
      dispatch(allowLocationSplashAction(true));
    }
  };

  handleSubmitBet = () => {
    const isSpecialGroup = get(this.props, "wagerType.specialGroup", false);
    const betSelectionsGroup = get(this.props, "betSelections", []);
    const race = get(this.props, "race");

    const specialSelections = getBetDetails({
      betType: get(this.props, "wagerType"),
      selections: isSpecialGroup
        ? getBetSelectionSpecialGroup(
            betSelectionsGroup,
            get(this.props, "wagerType.type.code")
          )
        : betSelectionsGroup,
      bettingInterests: get(this.props, "bettingInterests", []),
      betAmount: get(this.props, "betAmount", 0),
      betCount: 1
    });

    const betTotal = sumBy(specialSelections, "betTotal");

    if (
      typeof this.props.balance === "number" &&
      this.props.balance < betTotal
    ) {
      this.props.dispatch(
        setGroupBetTicketStatusAction(
          get(this.props, "wagerType.type.code"),
          BetStatus.error,
          {},
          BetError.insufficientFunds
        )
      );
      this.props.dispatch(setGroupBetTicketStopAction());
      this.showMessage(
        this.getMessageFormatted(
          BetError.insufficientFunds.toString(),
          false,
          true
        ),
        true
      );
      return;
    }

    const bets = specialSelections.map((bet) => {
      const betSelections = get(bet, "betSelections", []);
      const wagerTypeCode = get(bet, "wagerType.type.code", "");

      const wagerSelection = betSelections.map((column: string[]) =>
        column.map((selection: string) => ({ order: +selection }))
      );

      // --------- add overlay processing on betTicket
      this.props.dispatch(
        setGroupBetTicketStatusAction(wagerTypeCode, BetStatus.processing, {})
      );

      return async () => {
        const stop = get(this.props, "betTicketForceStopProcessing", false);
        let callback;
        if (stop) {
          this.props.dispatch(
            setGroupBetTicketStatusAction(wagerTypeCode, BetStatus.error, {})
          );
        } else {
          callback = this.submitBet({
            wagerType: get(bet, "wagerType"),
            betSelections,
            wagerSelection,
            betAmount: get(bet, "wagerAmount"),
            race
          });
        }

        return callback;
      };
    });

    asyncSubmit(bets);
  };

  submitBet = ({
    wagerType,
    betSelections,
    wagerSelection,
    betAmount,
    race
  }: SubmitBet) => {
    const betSelectionsNumbers = mapSelectionsToNumber(betSelections);
    const totals = calculateTotals(betAmount, wagerType, betSelectionsNumbers);
    const wagerTypeCode = get(wagerType, "type.code");

    const raceIdentify = {
      trackAbbr: get(race, "track.trackCode"),
      raceNumber: get(race, "raceNumber")
    };

    const betCost = betAmount * totals.betCount;

    performanceUtils.register("place_bet");
    // --------- add overlay processing on betTicket
    this.props.dispatch(
      setBetTicketState({
        isProcessing: true
      })
    );

    const startBetTime = performance.now();

    return wtx
      .placeBet(
        wagerSelection,
        wagerTypeCode,
        betAmount,
        betCost,
        raceIdentify,
        get(this.props, "geolocation.location"),
        get(this.props, "geolocation.regions"),
        this.props.accountNumber
      )
      .then((response) => {
        sendAppMetrics("frontend-fdm", this.props.accountNumber, [
          "bets_number",
          {
            name: "bet_placement_time",
            value: Math.floor(performance.now() - startBetTime)
          }
        ]);

        if (response.summary && response.summary.userBalance) {
          // new wtx approach
          this.props.dispatch(updateBalance(response.summary.userBalance));
        }

        if (multiLegWagerTypes.includes(wagerTypeCode)) {
          this.props.dispatch(setHasMultiLegWagersAction(true));
        }

        // ---------update betTicket group tag
        this.props.dispatch(
          setGroupBetTicketStatusAction(wagerTypeCode, BetStatus.success, {})
        );

        // react after store update
        setImmediate(() => {
          // --------- remove overlay processing on betTicket
          if (this.props.betTicketProcessing <= 0) {
            this.showMessage(this.getMessageFormatted());
          }

          this.props.dispatch(setBetConfirmation(true));

          this.setState({
            retryBetCounter: 0
          });

          performanceUtils.measure("place_bet");
          if (this.props.talentPick) {
            mediator.base.dispatch({
              type: "TP_PROGRAMPAGE_BET_SUCCESS",
              payload: {
                action: "Bet Success",
                label: get(this.props.talentPick, "wagerType.abbreviation"),
                talentPickOrigin: this.props.talentPickTriggerOrigin,
                tag: mapSelectionsToString(
                  get(this.props.talentPick, "selections", [])
                ),
                race: {
                  raceNumber: get(this.props.talentPick, "race"),
                  track: {
                    trackName: get(this.props.talentPick, "trackName")
                  },
                  type: {
                    code: get(this.props.talentPick, "raceType")
                  }
                },
                betId: get(wagerType, "type.id"),
                betAmount: +get(this.props.talentPick, "amount"),
                betType: get(this.props.talentPick, "wagerType.abbreviation")
              }
            });
          } else {
            AlchemerEvents.placeABet();
            mediator.base.dispatch({
              type: "BET_CONFIRMATION:BET_SUCCESS",
              payload: {
                selections: mapSelectionsToString(betSelections),
                trackName: get(race, "track.trackName"),
                raceNumber: get(race, "raceNumber"),
                betAmount,
                module: "Bet Ticket",
                betType: get(wagerType, "type.code"),
                betId: get(wagerType, "type.id"),
                runnerType:
                  get(race, "type.code") === "G" ? "Greyhound" : "Horse"
              }
            });
          }

          if (isMobile(this.tvgConf.product)) {
            sendToAppsFlyer({
              key: "af_bet_cost",
              values: {
                accountId: this.props.accountNumber.toString(),
                af_revenue: betCost.toString()
              }
            });

            mediator.ios.dispatch({
              type: "PUSHWOOSH_EVENTS",
              payload: {
                pushwooshTags: {
                  LastBet: (+(new Date().getTime() / 1000)).toFixed(0)
                }
              }
            });
          }
        });
      })
      .catch((error) => {
        sendAppMetrics("frontend-fdm", this.props.accountNumber, [
          "bets_number",
          "place_bets_error"
        ]);
        const bets = Object.keys(get(this.props, "betTicketGroupStatus") || {});
        const isSpecialGroup =
          get(this.props, "wagerType.specialGroup", false) && bets.length > 1;
        const errorCode = get(error.response, "data.code");

        if (errorCode === INELIGIBLE_RESIDENCY_STATE_FOR_RACING) {
          this.props.dispatch(openInvalidResidentialAddressModal());
          this.props.dispatch(setBetConfirmation(false));
          return;
        }

        if (errorCode === TERMS_AND_CONDITIONS_OR_ACKNOWLEDGES_NOT_ACCEPTED) {
          getUserJurisdiction().then(({ accepted }) => {
            if (!accepted) onOpenComplianceModal();
          });
        }

        if (
          errorCode === BetError.stateMismatch &&
          this.state.retryBetCounter < 3
        ) {
          getUserProfile(
            getAuthTokenCookie(),
            get(this.props, "geolocation.state"),
            this.props.dispatch
          )
            .then(() => {
              this.submitBet({
                wagerType,
                betSelections,
                wagerSelection,
                betAmount,
                race
              });
            })
            .catch((profileError) => {
              if (
                profileError === INELIGIBLE_RESIDENCY_STATE_FOR_RACING_MESSAGE
              ) {
                this.props.dispatch(openInvalidResidentialAddressModal());
                this.props.dispatch(setBetConfirmation(false));
              }
            });

          this.setState({
            retryBetCounter: this.state.retryBetCounter + 1
          });

          return;
        }

        // Disables retry bet button when receiving these errors
        if (
          errorCode === INELIGIBLE_LOCATION ||
          errorCode === GEO_COMPLY_REJECTED
        ) {
          this.props.dispatch(
            setGeolocationError("GEOLOCATION_REJECTED", [], false)
          );
        }

        if (
          errorCode === BetError.geoComplyTokenMissing ||
          errorCode === GEO_COMPLY_EXPIRED
        ) {
          onTriggerGeolocation();
        }

        this.props.dispatch(
          setGroupBetTicketStatusAction(
            wagerTypeCode,
            BetStatus.error,
            {
              wagerType,
              betSelections,
              wagerSelection,
              betAmount,
              race
            },
            errorCode
          )
        );

        // react after store update
        setImmediate(() => {
          if (errorCode === BetError.expiredToken) {
            this.props.dispatch(setBetConfirmation(false));
            if (isXSell()) {
              const { history, dispatch, geolocationError } = this.props;
              // $FlowFixMe
              clearSession({
                history,
                dispatch,
                geolocationError,
                allowRedirect: false
              });
            } else {
              logout();
              return;
            }
          }

          if (
            errorCode === BetError.insufficientFunds ||
            errorCode === BetError.raceOff
          ) {
            this.props.dispatch(setGroupBetTicketStopAction());
            this.showMessage(
              this.getMessageFormatted(errorCode, false),
              BetError.insufficientFunds === errorCode || isSpecialGroup
            );
          }

          if (this.props.betTicketProcessing <= 0) {
            const errorCodeProcessed = bets.reduce((errorCodeList, betKey) => {
              const statusCode = get(
                this.props,
                `betTicketGroupStatus.${betKey}.statusCode`
              );
              if (statusCode) {
                errorCodeList.add(statusCode);
              }
              return errorCodeList;
            }, new Set());

            const errorMessage = this.getMessageFormatted(
              errorCodeProcessed.size > 1 ? "default" : errorCode,
              true,
              false
            );

            if (this.props.isBetMessagesWithBehaviour) {
              let errorMessageText = get(
                errorMessage,
                "errorBetMessage.text",
                ""
              );
              const textVariables = words(
                errorMessageText,
                CAPIVariableRegex()
              );
              if (textVariables.length) {
                const errorResponseMessage = get(
                  error.response,
                  "data.message"
                );

                const betAmountLabel = this.props.talentPick
                  ? get(this.props.talentPick, "amount", 0).toFixed(2)
                  : betAmount.toFixed(2);
                const varNumbers = extractNumberOfText(errorResponseMessage, 2);

                errorMessageText = replaceCAPIVariables(errorMessageText, {
                  betAmount: betAmountLabel
                });

                varNumbers.forEach((varNumber, index) => {
                  const key = `number${index + 1}`;
                  const number = get(varNumbers, index, "0.00");
                  errorMessageText = replaceCAPIVariables(errorMessageText, {
                    [key]: number
                  });
                });

                set(errorMessage, "errorBetMessage.text", errorMessageText);
              }
              // TODO: Remove the IF below when the toggle isBetMessagesWithBehaviour gets removed
            } else if (errorCode === 136457) {
              const errorResponseMessage = get(
                error.response,
                "data.message",
                ""
              );
              let errorMessageText = get(errorMessage, "errorBetMessage", "");
              const replaceObj = {
                _betAmount: this.props.talentPick
                  ? get(this.props.talentPick, "amount", 0).toFixed(2)
                  : betAmount.toFixed(2),
                _minAmount: errorResponseMessage
                  .split("=")
                  .pop()
                  .split(")")[0]
                  .trim()
              };

              errorMessageText = errorMessageText.replace(
                /_betAmount|_minAmount/gi,
                (matched) => replaceObj[matched]
              );

              set(errorMessage, "errorBetMessage", errorMessageText);
            }

            const responsibleGamingErrorCodes = [
              BetError.wagerLimits,
              BetError.maxWagerSizeLimit
            ];

            this.showMessage(
              errorMessage,
              isSpecialGroup || responsibleGamingErrorCodes.includes(errorCode)
            );
          }

          if (this.props.talentPick) {
            mediator.base.dispatch({
              type: "TP_PROGRAMPAGE_BET_ERROR",
              payload: {
                action: "Bet Error",
                label: this.formatErrorMessage(errorCode).text,
                talentPickOrigin: this.props.talentPickTriggerOrigin,
                tag: mapSelectionsToString(
                  get(this.props.talentPick, "selections", [])
                ),
                race: {
                  raceNumber: get(this.props.talentPick, "race"),
                  track: {
                    trackName: get(this.props.talentPick, "trackName")
                  },
                  type: {
                    code: get(this.props.talentPick, "raceType")
                  }
                },
                betAmount: get(this.props.talentPick, "amount"),
                betType: get(this.props.talentPick, "wagerType.abbreviation")
              }
            });
          } else {
            mediator.base.dispatch({
              type: "BET_CONFIRMATION:BET_ERROR",
              payload: {
                error: get(error.response, "data.code"),
                module: this.props.showBetConfirmation
                  ? "Confirm Bet Modal"
                  : "Bet Ticket",
                selections: mapSelectionsToString(
                  get(this.props, "betSelections", [])
                ),
                trackName: get(race, "track.trackName"),
                raceNumber: get(race, "raceNumber"),
                betAmount,
                betType: get(wagerType, "type.code"),
                runnerType:
                  get(race, "type.code") === "G"
                    ? "Greyhounds Racing"
                    : "Horse Racing"
              }
            });
          }
        });
      })
      .finally(() => getWalletBalances(this.props.dispatch));
  };

  formatErrorMessage = (errorResponse: any, errorType: string = "default") => {
    const simpleMessage = typeof errorResponse !== "object";
    const errorMessage = simpleMessage
      ? errorResponse
      : get(errorResponse, errorType, errorResponse.default);
    const defaultValues = {
      text: get(
        errorMessage,
        "default.text",
        "An error occurred while trying to place your bet."
      ),
      buttonType: "retry"
    };
    const buttonType = get(
      errorResponse,
      "buttonType",
      defaultValues.buttonType
    );

    let errorFormatted;

    if (typeof errorMessage !== "object") {
      errorFormatted = {
        title: "",
        text: errorMessage || defaultValues.text,
        buttonType
      };
    } else {
      const text = get(errorMessage, `text`, defaultValues.text);
      const title = get(errorMessage, `title`);

      errorFormatted = {
        title,
        text,
        buttonType
      };
    }

    return errorFormatted;
  };

  parseErrorMessage = (
    code: number | string,
    errorType: string = "default"
  ) => {
    const defaultMessage = {
      title: "",
      text: "An error occurred while trying to place your bet."
    };
    const wtxErrorMessages = attempt(() =>
      JSON.parse(
        this.props.isBetMessagesWithBehaviour
          ? this.props.wtxErrorMessagesWithBehaviour
          : this.props.wtxErrorMessages
      )
    );

    const message = !isError(wtxErrorMessages)
      ? wtxErrorMessages[code]
      : defaultMessage;
    return this.props.isBetMessagesWithBehaviour
      ? this.formatErrorMessage(message, errorType)
      : message;
  };

  dismissTalentPicksWarning = () => {
    this.props.dispatch(noMandatoryTalentIdSelected(false));
  };

  openPotencialReturnModal = (hasMessage: boolean) => {
    this.props.dispatch({
      type: "OPEN_POTENCIAL_RETURN_MODAL",
      payload: { hasMessage, parentPage: "programPage" }
    });
  };

  openPickBetsHowItWorksModal = () => {
    onBoardingClickCTAGTM("help");
    this.props.dispatch({
      type: "OPEN_PICK_BETS_HOW_IT_WORKS_MODAL"
    });
  };

  placePick() {
    this.props.dispatch(noMandatoryTalentIdSelected(false));
    this.resetState();
    this.props.dispatch(setBetConfirmation(true));
    this.props.dispatch(
      setBetTicketState({
        betConfirmationTitle: "confirm and publish pick"
      })
    );
  }

  showMessage(message: BetTicketState, isGroup: boolean = false) {
    if (isGroup) {
      this.props.dispatch(setBetConfirmation(true));
      this.setForceBetConfirmation(true);
    }

    this.props.dispatch(setBetTicketState(message));
  }

  renderTalentHeader = () =>
    this.props.showBetConfirmation &&
    !this.props.pickEditButton &&
    !!get(this.props, "talentPick", null) &&
    get(this.props, "talentPick.talent.pictureUrl", "") !== ""
      ? () => (
          <React.Fragment>
            {!isXSell() &&
              this.props.geolocationStatus &&
              this.props.geoLocationToastMsg &&
              this.props.geoLocationToastMsg[this.props.geolocationStatus] && (
                <Toast
                  {...this.props.geoLocationToastMsg[
                    this.props.geolocationStatus
                  ]}
                  noAnimation={this.props.geolocationMessageShown}
                  callback={() => this.props.dispatch(geolocationStatusClear())}
                />
              )}
            <TalentPickConfirmationHeader
              {...(get(this.props, "talentPick.talent.pictureUrl", "") !== ""
                ? {
                    talentImage: `//${this.tvgConf.config(
                      "domain.desktop"
                    )}/${get(this.props, "talentPick.talent.pictureUrl", "")}`
                  }
                : {})}
              message={this.props.betTicketState.randomTalentPhrase || ""}
            />
          </React.Fragment>
        )
      : () =>
          !isXSell() &&
          this.props.geolocationStatus &&
          this.props.geoLocationToastMsg &&
          this.props.geoLocationToastMsg[this.props.geolocationStatus] && (
            <Toast
              {...this.props.geoLocationToastMsg[this.props.geolocationStatus]}
              noAnimation={this.props.geolocationMessageShown}
            />
          );

  render() {
    const { forceBetConfirm } = this.state;
    const geolocationStatus = get(this.props, "geolocationStatus");
    const geolocationError = get(this.props, "geolocationError", {});
    const isGeolocationErrorRetryable = get(
      this.props,
      "geolocationErrorRetryable"
    );
    const isLogged = get(this.props, "isLogged", false);
    const isSpecialGroup = get(this.props, "wagerType.specialGroup", false);
    const wagerType = get(this.props, "wagerType");
    let betSelections = get(this.props, "betSelections", []);
    let selectedRunners = [];
    let probableValue;

    if (isSpecialGroup) {
      const specialGroup = getBetSelectionSpecialGroup(
        betSelections,
        get(this.props, "wagerType.type.code")
      );
      betSelections = specialGroup;

      selectedRunners = specialGroup.allSelections;

      probableValue = getCurrentProbableValue(
        get(this.props, "probables"),
        wagerType,
        this.props.betAmount,
        get(this.props, "betSelections", []),
        true
      );
    } else {
      selectedRunners = betSelections;

      probableValue = getCurrentProbableValue(
        get(this.props, "probables"),
        wagerType,
        this.props.betAmount,
        betSelections,
        false
      );
    }

    const totals =
      this.props.isVisible && get(wagerType, "columnCount")
        ? calculateTotals(
            get(this.props, "betAmount"),
            wagerType,
            selectedRunners
          )
        : { betCount: 0 };

    const betTypeLabel =
      get(wagerType, "type.name", "").length > 6
        ? get(wagerType, "type.code")
        : get(wagerType, "type.name");

    const selectionsToShow = getBetSelectionsToShow(
      this.props.betSelections,
      get(this.props, "bettingInterests", [])
    );

    const videoFeedback = RaceHeaderUtils.getVideoFeedback(
      this.props.race,
      get(this.props, "races[0]"),
      get(this.props, `races[${get(this.props.races, "length", 1) - 1}]`),
      true
    );

    const isVerifyLocationEnable = isLogged && isGeolocationErrorRetryable;
    const isGeolocationError = isLogged && !isEmpty(geolocationError);
    const isPlaceBetDisabled =
      geolocationStatus === "LOCATION_IN_PROGRESS" ||
      (isGeolocationError && !isGeolocationErrorRetryable) ||
      !getGeoTokenCookie();
    const isBetDisabled =
      this.props.betAmount * totals.betCount <= 0 ||
      (isLogged && isPlaceBetDisabled);

    return (
      <Fragment>
        <FeedbackMessageContext.Provider
          value={
            this.props.geolocationStatus &&
            this.props.geoLocationToastMsg &&
            this.props.geoLocationToastMsg[this.props.geolocationStatus] && {
              ...this.props.geoLocationToastMsg[this.props.geolocationStatus],
              isMessageShown: this.props.geolocationMessageShown,
              showToast: isXSell()
                ? false
                : get(
                    this.props.geoLocationToastMsg,
                    // $FlowFixMe
                    `${this.props.geolocationStatus}.showToast`,
                    false
                  )
            }
          }
        >
          <BetTicket
            key="betTicketComponent"
            selections={selectionsToShow}
            betLeg={this.props.betLeg}
            onCancelBet={() => this.cancelBet(true)}
            onPlaceBet={this.handleBetNow}
            onChangeBetAmount={this.setBetAmount}
            onCloseCustomAmountModal={this.onCloseCustomAmountModal}
            betType={betTypeLabel}
            betTotal={this.props.betAmount * totals.betCount}
            betAmounts={get(wagerType, "wagerAmounts")}
            minimumWagerAmount={get(wagerType, "minWagerAmount")}
            selectedBetAmount={this.props.betAmount}
            raceTypeCode={get(this.props, "race.type.code")}
            isVisible={this.props.isVisible}
            isBetDisabled={isBetDisabled}
            isKey={get(wagerType, "isKey", false)}
            isLeg={get(wagerType, "legCount", 1) > 1}
            isMultiLeg={this.props.isMultiLeg}
            isProcessing={
              this.props.betTicketState.isProcessing ||
              this.props.isRequestingLocation
            }
            raceNumber={this.props.race.raceNumber}
            hasOtherAmount
            modalType={this.props.columnLayout === "single" ? "bottom" : "fade"}
            onWarningClose={this.handleCloseWarning}
            scratchedSelectionMessage={getTitleScratchedRunners(
              this.props.scratchedSelections
            )}
            onDismissError={() => this.resetState()}
            isSelectionScratched={
              !this.props.showBetConfirmation &&
              isScratchedSelections(this.props.scratchedSelections)
            }
            isBettingError={
              !forceBetConfirm &&
              !+this.props.showBetConfirmationPreference &&
              this.props.betTicketState.isBettingError
            }
            errorBetTitle={this.props.betTicketState.betConfirmationTitle}
            errorBetMessage={this.props.betTicketState.errorBetMessage}
            favoriteRunners={this.props.favoriteSelections}
            selectTalentIdErrorVisible={this.props.selectTalentIdErrorVisible}
            dismissTalentPicksWarning={this.dismissTalentPicksWarning}
            isMobile={this.props.columnLayout === "single"}
            inIosAppTablet={
              isMobile(this.tvgConf.product) && this.tvgConf.device === "tablet"
            }
            numberOfWager={get(totals, "betCount", 0)}
            currentProbable={probableValue}
            hasWagerTypeProbable={doesRaceWagerTypeHasProbables(
              get(this.props, "probables"),
              get(wagerType, "type.id", 0)
            )}
            openPotencialReturnModal={this.openPotencialReturnModal}
            potentialReturnProgramPage={this.props.potentialReturnProgramPage}
            openPickBetsHowItWorksModal={this.openPickBetsHowItWorksModal}
            isSpecialGroup={isSpecialGroup}
            onboardingTooltipShow={
              this.props.onboardingTooltipShow &&
              this.props.pickBetsOnboardingToggle
            }
            onCloseOnboardingTooltip={this.props.onCloseOnboardingTooltip}
            emptyWPSMessage={get(
              this.props,
              "descriptionsWinBetGroupMessage.EmptyTicket",
              ""
            )}
            toastCallback={() => this.props.dispatch(geolocationStatusClear())}
            isVerifyLocationEnable={isVerifyLocationEnable}
            onVerifyLocation={() => {
              this.props.dispatch(geolocationStatusClear());
              onTriggerGeolocation();
            }}
            isGeolocationErrorShown={isGeolocationError}
            geolocationError={this.props.geolocationBetErrorMsg}
            balance={this.props.balance}
            isLogged={this.props.isLogged}
            dispatch={this.props.dispatch}
            isAccountCompliant={this.props.isAccountCompliant}
            unverifiedUserBetErrorMsg={this.props.unverifiedUserBetErrorMsg}
            openDepositModal={onTriggerDeposit}
            openVerifyUserModal={onOpenComplianceModal}
            isBalanceShown={this.props.isBalanceShown}
          />
        </FeedbackMessageContext.Provider>
        <BetConfirmationModal
          race={this.props.race}
          wagerType={this.props.wagerType}
          betSelections={betSelections}
          selections={this.props.betSelections}
          betAmount={this.props.betAmount}
          showBetConfirmation={this.props.showBetConfirmation}
          isVideoShown={this.props.isVideoShown}
          betGroupStatus={this.props.betTicketGroupStatus}
          isProcessing={this.props.betTicketState.isProcessing}
          isBetPlaced={this.props.betTicketState.isBetPlaced}
          isBettingError={this.props.betTicketState.isBettingError}
          title={this.props.betTicketState.betConfirmationTitle}
          errorBetMessage={this.props.betTicketState.errorBetMessage}
          bettingInterests={this.props.bettingInterests}
          isOpen={this.props.showBetConfirmation}
          onRetain={this.onRetainRunners}
          onRetry={this.onRetryBets}
          onBackToRace={this.onBackToRace}
          onConfirmation={this.handleSubmitBet}
          resetState={this.resetState}
          modalType={this.props.columnLayout === "single" ? "bottom" : "fade"}
          modalTopChildren={this.renderTalentHeader()}
          favoriteRunners={this.props.favoriteSelections}
          repetitionsNr={this.props.betTicketState.repeatBetNumber}
          forcedStop={get(this.props, "betTicketForceStopProcessing", false)}
          hasVideo={
            this.props.watchButtonBetPlacedToggle &&
            videoFeedback &&
            videoFeedback.indexOf("AVAILABLE") === 0
          }
          watchVideoActive={this.props.watchButtonBetPlacedToggle}
          errorBets={this.props.betTicketErrorBets}
          scratchedSelectionMessage={getTitleScratchedRunners(
            this.props.scratchedSelections
          )}
          isSelectionScratched={
            this.props.showBetConfirmation &&
            isScratchedSelections(this.props.scratchedSelections)
          }
          onWarningClose={this.handleCloseWarning}
          isBetDisabled={isBetDisabled}
        />
      </Fragment>
    );
  }
}

export default connect(
  (
    store,
    props: {
      isVisible: boolean,
      isVideoShown: boolean,
      bettingInterests: BettingInterest[][],
      race: RaceProgram,
      wagerType: RaceWagerType,
      betSelections: string[][],
      columnLayout: "single" | "dual",
      betAmount: number,
      showBetConfirmation: boolean,
      scratchedSelections: string[][]
    }
  ) => ({
    race: props.race,
    wagerType: props.wagerType,
    columnLayout: props.columnLayout,
    betSelections: props.betSelections,
    betAmount: props.betAmount,
    showBetConfirmation: props.showBetConfirmation,
    scratchedSelections: props.scratchedSelections,
    isVideoShown: props.isVideoShown,
    isLogged: store.userData.logged,
    isAccountCompliant: isAccountCompliantSelector(store),
    accountNumber: getAccountNumber(store),
    hasUserPreferences: get(store, "userData.gotPreferences"),
    showBetConfirmationPreference: get(
      store,
      "userData.preferences.show_bet_confirm",
      "1"
    ),
    selectTalentIdErrorVisible: get(
      store,
      "TalentProgram.selectTalentIdErrorVisible",
      false
    ),
    bettingInterests: get(
      store,
      "RaceProgram.currentRace.bettingInterests",
      []
    ),
    geolocation: store.geolocation,
    locationStatus: get(store, "locationSplash.status"),
    isLocationRequired: get(store, "locationSplash.isLocationRequired"),
    isRequestingLocation: get(store, "locationSplash.isLoading"),
    wtxErrorMessages: get(store, "capi.messages.wtxErrorMessages"),
    wtxErrorMessagesWithBehaviour: get(
      store,
      "capi.messages.wtxErrorMessagesWithBehaviour"
    ),
    isBetMessagesWithBehaviour: get(
      store,
      "capi.featureToggles.isBetMessagesWithBehaviour",
      false
    ),
    pickEditButton: get(store, "RaceProgram.pickEditButton", true),
    betTicketGroupStatus: store.BetTicket.betGroupStatus,
    betTicketState: store.BetTicket.config,
    betTicketProcessing: store.BetTicket.processingBets,
    betTicketForceStopProcessing: store.BetTicket.forceStop,
    betTicketErrorBets: store.BetTicket.errorBets,
    talentPickTriggerOrigin: get(
      store,
      "RaceProgram.talentPickTriggerOrigin",
      null
    ),
    betLeg: get(store, "RaceProgram.betLeg"),
    potentialReturnProgramPage: get(
      store,
      "capi.featureToggles.potentialReturnProgramPage",
      false
    ),
    pickBetsOnboardingToggle: get(
      store,
      "capi.featureToggles.pickBetsOnboarding",
      false
    ),
    balance: getBalance(store),
    descriptionsWinBetGroupMessage: parseJSONCapiMessage(
      store,
      "capi.messages.descriptionsWinBetGroup",
      {
        WP: "",
        WPS: "",
        INT: "",
        EmptyTicket: "",
        WS: ""
      }
    ),
    geolocationStatus: getGeolocationStatus(store),
    geolocationError: getGeolocationError(store),
    geolocationErrorRetryable: getGeolocationErrorRetryable(store),
    geolocationMessageShown: getGeolocationMessageShown,
    geoLocationToastMsg: parseJSONCapiMessage(
      store,
      "capi.messages.geoLocationToastMsg",
      {}
    ),
    geolocationBetErrorMsg: parseJSONCapiMessage(
      store,
      "capi.messages.geoLocationBetErrorMsg",
      {}
    ),
    unverifiedUserBetErrorMsg: parseJSONCapiMessage(
      store,
      "capi.messages.unverifiedUserBetErrorMsg",
      {}
    ),
    isBalanceShown:
      get(store, "userData.preferences.balance_is_shown", "0") !== "0"
  }),
  (dispatch: Dispatch<*>) => ({ dispatch })
)(BetTicketComponent);
